import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import { CurrencyProvider } from './i18n/CurrencyContext';
import { WishlistProvider } from './context/WishlistContext';
import { AuthProvider } from './context/AuthContext';
import { AffiliateProvidersProvider } from './context/AffiliateProvidersContext';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { ExperiencesSection } from './components/ExperiencesSection';
import { EventsSection } from './components/EventsSection';
import { CategoriesSection } from './components/CategoriesSection';
import { PlacesToVisitSection } from './components/PlacesToVisitSection';
import { WhatToDoSection } from './components/WhatToDoSection';
import { splitHomepageRows } from './lib/splitHomepageRows';
import { LocationsSection } from './components/LocationsSection';
import { SitgesGuidePreviewSection } from './components/SitgesGuidePreviewSection';
import { Footer } from './components/Footer';
import { SkeletonGrid } from './components/SkeletonCard';
import { ExperiencesPage } from './pages/ExperiencesPage';
import { SitgesGuidePage } from './pages/SitgesGuidePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { LegalNoticePage } from './pages/LegalNoticePage';
import { NotFoundPage } from './pages/NotFoundPage';
import AdminLogin from './pages/AdminLogin';
import { ScrollToTop } from './components/ScrollToTop';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { SEOHead } from './components/SEOHead';
import { ErrorBoundary } from './components/ErrorBoundary';
import { buildItemListJsonLd } from './lib/seoJsonLd';
import { Toaster } from './components/ui/sonner';
import { track } from './lib/analytics';
import './App.css';
import { BACKEND_URL } from './lib/apiBase';
import { toUrlCategorySlug } from './lib/categoryUrlSlug';

// Admin surface is code-split: TipTap, recharts and the full admin form
// only ship to logged-in admins, not to public visitors. Cuts the public
// JS bundle roughly in half.
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const CategoryImagesManager = lazy(() => import('./pages/admin/CategoryImagesManager'));
const AnalyticsDashboard = lazy(() => import('./pages/admin/AnalyticsDashboard'));
const CurrencyRatesAdmin = lazy(() => import('./pages/admin/CurrencyRatesAdmin'));
const SitgesGuideAdmin = lazy(() => import('./pages/admin/SitgesGuideAdmin'));
const AffiliateButtonsManager = lazy(() => import('./pages/admin/AffiliateButtonsManager'));

// On a full page reload, wipe any `?search=` URL param so the search input
// always starts empty regardless of which page the user reloads on. Runs
// once at module-load time, before any component mounts and reads the URL.
try {
  const nav = (typeof performance !== 'undefined' && performance.getEntriesByType)
    ? performance.getEntriesByType('navigation')[0]
    : null;
  if (nav && nav.type === 'reload' && typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    if (url.searchParams.has('search')) {
      url.searchParams.delete('search');
      window.history.replaceState({}, '', url.toString());
    }
  }
} catch (_) {
  // Performance API unavailable — fail silently.
}

function HomePage() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [experiences, setExperiences] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [activeDestination, setActiveDestination] = useState('');
  const [showStickySearch, setShowStickySearch] = useState(false);
  const [stickySearchQuery, setStickySearchQuery] = useState('');

  // Show sticky search on mobile when scrolling
  useEffect(() => {
    const handleScroll = () => {
      // Show sticky search after scrolling past 300px on mobile
      setShowStickySearch(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mobile keyboard dismiss — when the user actually drags a finger to
  // scroll the homepage, blur whatever search input is currently focused
  // so the on-screen keyboard collapses. We listen ONLY to `touchmove`
  // (real finger motion) and not to `scroll`, because mobile browsers fire
  // `scroll` for layout-induced reflows too (address bar appearing,
  // keyboard opening) — those would otherwise blur the input the user
  // just tapped.
  useEffect(() => {
    const dismissKeyboard = () => {
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
        el.blur();
      }
    };
    window.addEventListener('touchmove', dismissKeyboard, { passive: true });
    return () => window.removeEventListener('touchmove', dismissKeyboard);
  }, []);

  const handleStickySearch = (e) => {
    e.preventDefault();
    // Navigate to Explore page regardless of search term
    if (stickySearchQuery.trim()) {
      navigate(`/Explore?search=${encodeURIComponent(stickySearchQuery.trim())}`);
    } else {
      navigate('/Explore');
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const [expRes, catRes] = await Promise.all([
          // No `?lang=…` projection: we want every translation baked into the
          // payload so language switches stay client-side and don't flash
          // through English while a per-language refetch is in-flight.
          fetch(`${BACKEND_URL}/api/experiences`),
          fetch(`${BACKEND_URL}/api/categories`),
        ]);
        const [expData, catData] = await Promise.all([expRes.json(), catRes.json()]);
        setExperiences(expData.experiences || []);
        setCategories(catData.categories || []);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // Mount-only fetch; switching language no longer triggers a refetch.
  }, []);

  const fetchExperiences = useCallback(async (query = '', category = '', destination = '') => {
    try {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (category) params.set('category', category);
      if (destination) params.set('destination', destination);
      const url = params.toString()
        ? `${BACKEND_URL}/api/experiences?${params.toString()}`
        : `${BACKEND_URL}/api/experiences`;
      const res = await fetch(url);
      const data = await res.json();
      setExperiences(data.experiences || []);
    } catch (err) {
      console.error('Failed to fetch experiences:', err);
    }
  }, []);

  const handleCategoryClick = useCallback((slug) => {
    // Navigate to Explore page with category filter
    navigate(`/Explore?category=${toUrlCategorySlug(slug)}`);
  }, [navigate]);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setActiveCategory('');
    setActiveDestination('');
    fetchExperiences('', '', '');
  }, [fetchExperiences]);

  const handleNavigate = useCallback((section) => {
    if (section === 'experiences') navigate('/Explore');
    else if (section === 'categories') navigate('/Explore');
    else document.getElementById(section)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [navigate]);

  // Partition experiences into two non-overlapping pools so the
  // "Places to visit" and "Things to do" rows never show the same card.
  // Cards eligible for both rows are alternated between them.
  const { placesPool, thingsPool } = useMemo(
    () => splitHomepageRows(experiences),
    [experiences],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-[110px] pb-20" data-testid="home-loading-skeleton">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="h-9 w-56 rounded bg-[#eaf3fa] animate-pulse mb-7" />
          <SkeletonGrid count={8} />
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="SitgesVibes — Tours & Experiences in Sitges, Barcelona & Beyond"
        description="Discover the best tours, food, beaches and nightlife in Sitges, Barcelona, Tarragona and beyond — handpicked experiences with local insider knowledge."
        jsonLd={[
          buildItemListJsonLd(
            experiences.filter((e) => e.featured).length > 0
              ? experiences.filter((e) => e.featured)
              : experiences,
            { name: 'Featured experiences in Sitges', limit: 20 },
          ),
        ]}
      />
      {/* Sticky Search Bar (Mobile Only) */}
      <div 
        className={`lg:hidden fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          showStickySearch ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="px-[9px] py-2">
          <form onSubmit={handleStickySearch} className="relative">
            <input
              type="text"
              value={stickySearchQuery}
              onChange={(e) => setStickySearchQuery(e.target.value)}
              onFocus={(e) => e.target.select()}
              placeholder={t('search_experiences')}
              className="w-full h-[50px] pl-4 pr-[90px] text-[15px] font-bold text-[#1a4d7a] rounded-full
                         bg-white border border-[#1a4d7a]
                         placeholder:text-[#2d5f8d]/60 placeholder:font-medium
                         focus:outline-none focus:ring-2 focus:ring-[#0184ff]/20 focus:border-[#0184ff]
                         transition-all duration-200 shadow-sm"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            />
            <button
              type="submit"
              className="absolute right-1 top-1/2 -translate-y-1/2 
                         h-[42px] px-7 rounded-full
                         bg-[#3c84ff] hover:bg-[#2a6fe0]
                         flex items-center justify-center
                         transition-all duration-200
                         text-white font-semibold text-[14px]"
              aria-label={t('search')}
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t('search')}
            </button>
          </form>
        </div>
      </div>

      <main>
        <HeroSection onNavigate={handleNavigate} />
        <ExperiencesSection
          experiences={experiences}
          onClear={handleClearFilters}
          activeCategory={activeCategory}
          activeDestination={activeDestination}
          searchQuery={searchQuery}
        />
        <EventsSection experiences={experiences} />
        <CategoriesSection
          categories={categories}
          onCategoryClick={handleCategoryClick}
        />
        <PlacesToVisitSection experiences={placesPool} />
        <WhatToDoSection experiences={thingsPool} />
        <LocationsSection />
        <SitgesGuidePreviewSection />
      </main>
      <Footer />
    </>
  );
}

function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const [showHeaderSearch, setShowHeaderSearch] = useState(false);
  // Debounce timer for the search-input → URL navigation. The visible input
  // value updates synchronously via `setHeaderSearchQuery`, but the actual
  // URL change (which cascades through ExperiencesPage's filter useEffect
  // and re-renders the entire grid) is delayed so fast typing produces
  // exactly ONE filter pass instead of one per keystroke.
  const searchDebounceRef = useRef(null);
  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  // Prevent the browser from navigating to a file URL when the user drops an
  // image (or any file) onto the page outside of a designated drop zone. Rich
  // editors (TipTap) and our image-upload buttons use the file picker, never
  // the window-level drop, so this is safe and matches the common pattern
  // used to stop the "page refresh" caused by accidental drops.
  useEffect(() => {
    const onDragOver = (e) => {
      if (e.defaultPrevented) return;
      e.preventDefault();
    };
    const onDrop = (e) => {
      if (e.defaultPrevented) return;
      e.preventDefault();
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  // Hide the public SitgesVibes header on admin/auth routes \u2014 those have their own shell.
  const isAdminRoute =
    location.pathname.startsWith('/admin') || location.pathname.startsWith('/auth/');

  // Fire a page_view event on every route change (de-bounced inside track()).
  // Admin and auth routes are excluded to keep analytics focused on public UX.
  useEffect(() => {
    if (!isAdminRoute) {
      track('page_view', { page: location.pathname });
    }
  }, [location.pathname, isAdminRoute]);

  // Pages with filters that should show search in header
  const isFilterPage =
    location.pathname === '/Explore' || location.pathname.startsWith('/experience/');

  // Static content pages that should always show the header search bar
  const alwaysSearchPages = ['/Sitges-Guide', '/privacy-policy', '/cookie-policy', '/legal-notice'];
  const isAlwaysSearchPage = alwaysSearchPages.includes(location.pathname);

  // Sync header search with URL params on filter pages
  useEffect(() => {
    if (isFilterPage) {
      const searchParams = new URLSearchParams(location.search);
      const searchFromUrl = searchParams.get('search') || '';
      setHeaderSearchQuery(searchFromUrl);
    } else {
      // Clear search query when not on filter pages (including homepage)
      setHeaderSearchQuery('');
    }
  }, [location.pathname, location.search, isFilterPage]);

  // Track scroll on homepage to show/hide header search
  useEffect(() => {
    if (location.pathname === '/') {
      // Immediately set based on current scroll position
      setShowHeaderSearch(window.scrollY > 300);
      
      const handleScroll = () => {
        // Show search in header when scrolled past 300px
        setShowHeaderSearch(window.scrollY > 300);
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    } else if (isFilterPage || isAlwaysSearchPage) {
      // Always show search on filter pages and static policy pages
      setShowHeaderSearch(true);
    } else {
      setShowHeaderSearch(false);
    }
  }, [location.pathname, isFilterPage, isAlwaysSearchPage]);

  const handleHeaderSearchChange = (e) => {
    const value = e.target.value;
    setHeaderSearchQuery(value);

    // Live search: update URL on Explore page. Typing in the search box
    // also clears any active category filter so the two filter modes never
    // compete with each other. When the user starts typing from an
    // /experience/:slug detail page, we bounce them to /Explore with the
    // query so they land on real search results instead of staying on the
    // detail page.
    //
    // The actual `navigate()` is debounced ~180ms so a burst of keystrokes
    // produces one filter pass (and one full grid re-render) instead of
    // one per character. The visible input value is already in state, so
    // it never appears to lag.
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const path = location.pathname;
    const search = location.search;
    searchDebounceRef.current = setTimeout(() => {
      if (path === '/Explore') {
        const params = new URLSearchParams(search);
        if (value.trim()) {
          params.set('search', value.trim());
        } else {
          params.delete('search');
        }
        params.delete('category');
        navigate(`/Explore?${params.toString()}`, { replace: true });
      } else if (path.startsWith('/experience/')) {
        const q = value.trim();
        if (q) {
          navigate(`/Explore?search=${encodeURIComponent(q)}`, { replace: true });
        } else {
          navigate('/Explore', { replace: true });
        }
      }
    }, 180);
  };

  const handleHeaderSearchSubmit = (e) => {
    e.preventDefault();
    // User pressed Enter — flush any pending debounced URL change so we
    // navigate immediately with the latest typed value.
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    const q = headerSearchQuery.trim();
    if (q) {
      track('search_query', { query: q, page: 'header' });
      navigate(`/Explore?search=${encodeURIComponent(q)}`);
    } else {
      navigate('/Explore');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {!isAdminRoute && (
        <Header 
          showSearch={showHeaderSearch}
          searchQuery={headerSearchQuery}
          onSearchChange={handleHeaderSearchChange}
          onSearchSubmit={handleHeaderSearchSubmit}
        />
      )}
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/Explore" element={<ExperiencesPage />} />
          <Route path="/experience/:slug" element={<ExperiencesPage />} />
          <Route path="/Sitges-Guide" element={<SitgesGuidePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/legal-notice" element={<LegalNoticePage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-[#1a4d7a]">Loading admin…</div>}>
                  <AdminLayout />
                </Suspense>
              </ProtectedAdminRoute>
            }
          >
            <Route index element={<Navigate to="experiences" replace />} />
            <Route path="experiences" element={<Suspense fallback={null}><AdminPanel initialView="list" /></Suspense>} />
            <Route path="experiences/create" element={<Suspense fallback={null}><AdminPanel initialView="create" /></Suspense>} />
            <Route path="categories" element={<Suspense fallback={null}><CategoryImagesManager /></Suspense>} />
            <Route path="affiliate-buttons" element={<Suspense fallback={null}><AffiliateButtonsManager /></Suspense>} />
            <Route path="sitges-guide" element={<Suspense fallback={null}><SitgesGuideAdmin /></Suspense>} />
            <Route path="analytics" element={<Suspense fallback={null}><AnalyticsDashboard /></Suspense>} />
            <Route path="currency" element={<Suspense fallback={null}><CurrencyRatesAdmin /></Suspense>} />
          </Route>
          {/* Catch-all 404 — must remain the LAST route so real paths still
              match first. NotFoundPage emits `noindex,nofollow` so Google
              purges these dead URLs from its index. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <CurrencyProvider>
        <AuthProvider>
          <AffiliateProvidersProvider>
            <WishlistProvider>
              <BrowserRouter>
                <ScrollToTop />
                <AppLayout />
                <CookieConsentBanner />
              </BrowserRouter>
            </WishlistProvider>
          </AffiliateProvidersProvider>
        </AuthProvider>
      </CurrencyProvider>
    </LanguageProvider>
  );
}

export default App;
